import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, MailIcon, MapPinIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { site } from '../../data/site';
import { programLinks, schoolLinks } from '../../data/navigation';
import { ButtonLink } from '../ui/ButtonLink';
import { Container } from '../ui/Container';
import { MobileMenu } from './MobileMenu';
export function Header() {
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState<string | null>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const pathname = location.pathname.toLowerCase().replace(/\/+$/, '') || '/';
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (mq.matches) setOpen(false);else setDropdown(null);
    };
    const outside = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setDropdown(null);
    };
    mq.addEventListener('change', onChange);
    document.addEventListener('pointerdown', outside);
    return () => {
      mq.removeEventListener('change', onChange);
      document.removeEventListener('pointerdown', outside);
    };
  }, []);
  const navClass = 'inline-flex min-h-[44px] items-center gap-1.5 text-[15px] font-medium text-white/80 hover:text-white';
  return <>
    <a href="#main" className="sr-only z-[60] rounded bg-brand px-4 py-3 font-semibold text-white focus:not-sr-only focus:fixed focus:left-4 focus:top-4" data-id="element-413" data-mp="3p11o1jswuchu">Skip to content</a>
    <div className="on-dark hidden bg-black text-[13px] text-white/70 sm:block" data-id="element-414" data-mp="3u2hbfiik8v3e">
      <Container className="flex h-9 items-center justify-between gap-6" data-id="element-415" data-mp="1y598w2fmoce">
        <a href={site.directionsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white" data-id="element-416" data-mp="19942a44l8iux"><MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-417" data-mp="1d9xqkylxzzoc" />{site.street}, {site.cityLine}<span className="sr-only" data-id="element-418" data-mp="3aatsderaxara"> (opens directions in a new tab)</span></a>
        <div className="flex items-center gap-6" data-id="element-419" data-mp="35e3gl3mj51bo">
          <a href={site.phoneHref} className="inline-flex items-center gap-2 hover:text-white" data-id="element-420" data-mp="35fmuinkpr0aa"><PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-421" data-mp="3jca1zkaal8ug" />{site.phoneDisplay}</a>
          <a href={site.emailHref} className="hidden items-center gap-2 hover:text-white md:inline-flex" data-id="element-422" data-mp="3eei0byjui8d2"><MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-423" data-mp="2qoxgldfvb72q" />{site.email}</a>
        </div>
      </Container>
    </div>
    <header className="on-dark sticky top-0 z-50 border-b border-white/10 bg-ink text-white" data-id="element-424" data-mp="2mkpqbbtrhj2e">
      <Container className="flex h-[76px] items-center justify-between gap-4" data-id="element-425" data-mp="30fifl1zgedm6">
        <a href="/" aria-label="Bates Taekwondo homepage" className="flex shrink-0 items-center gap-3" onClick={close} data-id="element-426" data-mp="2vmpagd02p39k">
          <img src={site.logo} alt="" className="h-[60px] w-auto" width={48} height={60} data-id="element-427" data-mp="27vtjtj7cse3w" />
          <span className="font-display text-[1.35rem] font-bold uppercase leading-[0.95] tracking-wide" data-id="element-428" data-mp="22yjmlhdq2y5i">Bates<br data-id="element-429" data-mp="ifhid7w9asrs" />Taekwondo</span>
        </a>
        <nav ref={navRef} aria-label="Primary" className="hidden lg:block" data-id="element-430" data-mp="n792p0otk29h">
          <ul className="flex items-center gap-5 xl:gap-8" data-id="element-431" data-mp="n9c22bbv37qv">
            {[{
              label: 'Programs',
              links: programLinks
            }, {
              label: 'Schedule',
              href: '/schedule'
            }, {
              label: 'Our Coaches',
              href: '/instructors'
            }, {
              label: 'More Info',
              links: schoolLinks
            }].map(item => <li key={item.label} className="relative" onBlur={e => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropdown(null);
            }} onKeyDown={e => {
              if (e.key === 'Escape') {
                setDropdown(null);
                e.currentTarget.querySelector('button')?.focus();
              }
            }} data-id="element-432" data-mp="125tb2vwkwaa9">
              {item.links ? <>
                <button type="button" aria-expanded={dropdown === item.label} aria-controls={`nav-${item.label.replace(' ', '-')}`} className={navClass} onClick={() => setDropdown(dropdown === item.label ? null : item.label)} data-id="element-433" data-mp="x4m3awufrb3l">{item.label}<ChevronDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-434" data-mp="3hxh4ihcdvkob" /></button>
                {dropdown === item.label && <ul id={`nav-${item.label.replace(' ', '-')}`} className="absolute right-0 top-full min-w-[265px] rounded border border-white/15 bg-ink p-2 shadow-xl" data-id="element-435" data-mp="11gajgj25jd8">
                  {item.links.map(link => <li key={link.href} data-id="element-436" data-mp="3odh9or95fo7g"><a href={link.href} aria-current={pathname === link.href ? 'page' : undefined} className="flex min-h-[44px] items-center rounded px-3 py-2 text-sm text-white/85 hover:bg-white/10 hover:text-white" data-id="element-437" data-mp="3oav2wtu31cqa">{link.label}</a></li>)}
                </ul>}
              </> : <a href={item.href} aria-current={pathname === item.href ? 'page' : undefined} className={navClass} data-id="element-438" data-mp="fyqtfay15riz">{item.label}</a>}
            </li>)}
          </ul>
        </nav>
        <div className="flex items-center gap-2" data-id="element-439" data-mp="601arfyb831o">
          <ButtonLink href="/free-trial-class" size="md" className="hidden md:inline-flex" data-id="element-440" data-mp="61cfpj3hrgud">Book a Free Trial Class</ButtonLink>
          <button ref={toggleRef} type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} className="inline-flex h-12 w-12 items-center justify-center rounded text-white hover:bg-white/10 lg:hidden" data-id="element-441" data-mp="2qpqofpna1iss">{open ? <XIcon className="h-6 w-6" data-id="element-442" data-mp="2liaff9eh18h5" /> : <MenuIcon className="h-6 w-6" data-id="element-443" data-mp="2qj6ixluwusm1" />}</button>
        </div>
      </Container>
      <MobileMenu open={open} onClose={close} toggleRef={toggleRef} data-id="element-444" data-mp="2va6fgq04b8o0" />
    </header>
  </>;
}